import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { LuCheck, LuMinus, LuPlus } from 'react-icons/lu';

import { TimeSegmentInput } from './TimeSegmentInput';
import type { DateTimePickerProps } from './types';
import { Calendar } from '../../general/Calendar';
import { COMPONENTS_NAMESPACE } from '../../locales';
import { cn } from '../../utils/cn';
import { Button } from '../Button';

export type { DateTimePickerProps } from './types';

const MINUTE_MS = 60 * 1000;
const HOURS_MAX = 23;
const MINUTES_MAX = 59;
// react-day-picker's year dropdown ends at the current year unless endMonth is set
const YEAR_RANGE = 10;

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const withTime = (date: Date, hours: number, minutes: number) => {
    const next = new Date(date);
    next.setHours(hours, minutes, 0, 0);
    return next;
};

/**
 * A calendar with a time row (− HH : MM +) and an accept button for picking a point in time.
 *
 * Controlled: every edit calls `onChange`, the accept button calls `onAccept`.
 *
 * @param value - The currently picked date and time.
 * @param onChange - Callback fired with every edited date and time.
 * @param onAccept - Callback fired when the accept button is clicked.
 * @param minDate - Earliest selectable point in time; earlier days and months are disabled and an earlier value cannot be accepted.
 * @param minuteStep - Positive whole number of minutes added or subtracted by the +/- buttons. Defaults to 1.
 * @param title - Optional heading rendered above the calendar.
 * @param acceptLabel - Label of the accept button. Defaults to the translated "Übernehmen".
 * @param acceptIcon - Icon of the accept button. Defaults to a check mark.
 * @param disabled - Whether the accept button is disabled.
 * @param className - Additional CSS classes for the container.
 * @param calendarClassName - Additional CSS classes for the calendar (e.g. `p-0` to align the accept button with the days).
 *
 * @example
 * <DateTimePicker value={date} onChange={setDate} onAccept={save} minDate={new Date()} minuteStep={5} />
 */
export const DateTimePicker: React.FC<DateTimePickerProps> = ({
    acceptIcon,
    acceptLabel,
    calendarClassName,
    className,
    disabled,
    minDate,
    minuteStep = 1,
    onAccept,
    onChange,
    title,
    value,
}) => {
    const { t } = useTranslation(COMPONENTS_NAMESPACE);
    const [month, setMonth] = useState<Date>(value);

    const today = new Date();
    const isBeforeMin = !!minDate && value.getTime() < minDate.getTime();

    const handleDaySelect = (day: Date | undefined) => {
        if (!day) return;
        onChange(withTime(day, value.getHours(), value.getMinutes()));
    };

    const shift = (minutes: number) => {
        const next = new Date(value.getTime() + minutes * MINUTE_MS);
        onChange(next);
        setMonth(next);
    };

    return (
        <div className={cn('flex flex-col items-center gap-4', className)}>
            {title && <span className="pt-3 text-sm font-medium text-midnight">{title}</span>}
            <Calendar
                mode="single"
                selected={value}
                onSelect={handleDaySelect}
                month={month}
                onMonthChange={setMonth}
                disabled={minDate ? { before: startOfDay(minDate) } : undefined}
                startMonth={minDate ?? new Date(today.getFullYear() - YEAR_RANGE, 0)}
                endMonth={new Date(today.getFullYear() + YEAR_RANGE, 11)}
                className={cn('shadow-none', calendarClassName)}
                showDropdowns
            />
            <div className="flex items-center gap-1.5">
                <Button
                    type="button"
                    ariaLabel={t('dateTimePicker.decrementAriaLabel')}
                    className="size-8"
                    iconLeft={<LuMinus className="size-4" />}
                    onClick={() => shift(-minuteStep)}
                />
                <TimeSegmentInput
                    ariaLabel={t('dateTimePicker.hoursAriaLabel')}
                    max={HOURS_MAX}
                    value={value.getHours()}
                    onChange={(hours) => onChange(withTime(value, hours, value.getMinutes()))}
                />
                <span className="text-sm text-midnight">:</span>
                <TimeSegmentInput
                    ariaLabel={t('dateTimePicker.minutesAriaLabel')}
                    max={MINUTES_MAX}
                    value={value.getMinutes()}
                    onChange={(minutes) => onChange(withTime(value, value.getHours(), minutes))}
                />
                <Button
                    type="button"
                    ariaLabel={t('dateTimePicker.incrementAriaLabel')}
                    className="size-8"
                    iconLeft={<LuPlus className="size-4" />}
                    onClick={() => shift(minuteStep)}
                />
            </div>
            <Button
                type="button"
                className="w-full"
                disabled={disabled || isBeforeMin}
                iconLeft={acceptIcon ?? <LuCheck className="size-3.5" />}
                onClick={onAccept}
            >
                {acceptLabel ?? t('dateTimePicker.accept')}
            </Button>
            {isBeforeMin && (
                <span className="-mt-2 text-[10px] leading-[1.46] text-alert-error">
                    {t('dateTimePicker.beforeMinDate')}
                </span>
            )}
        </div>
    );
};
